import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { StatusCodes } from 'http-status-codes';

import fetchFileWithMimeType from 'utils/api/fetchFileWithMimeType';
import isS3SignedUrl from 'utils/api/isS3SignedUrl';
import { revokeObjectUrl } from 'utils/urlUtils';

import { MimeTypes } from 'constants/enums';
import { NO_USER_PERMISSION_MESSAGE_TEXT } from 'constants/notifications';

import useErrorCatch from './useErrorCatch';

const useFileLoader = (url: string, onError?: () => void) => {
  const { t } = useTranslation();
  const catchError = useErrorCatch();
  const [data, setData] = useState<string | null>(null);
  const [fileType, setFileType] = useState<MimeTypes | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  const revokeCurrentObjectUrl = useCallback(() => {
    if (objectUrlRef.current) {
      revokeObjectUrl(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      revokeCurrentObjectUrl();
    };
  }, [revokeCurrentObjectUrl]);

  const loadFile = useCallback(async () => {
    try {
      if (!url || isLoading) {
        return { data: null, fileType: undefined };
      }
      setIsLoading(true);

      const isS3Url = isS3SignedUrl(url);

      // For S3 signed URLs use direct URL to avoid CORS restrictions on fetch().
      if (isS3Url) {
        setData(url);
        setFileType(undefined);
        return { data: url, fileType: undefined };
      }

      const { blob, fileType } = await fetchFileWithMimeType(url);

      setFileType(fileType);
      revokeCurrentObjectUrl();
      const objUrl = window.URL.createObjectURL(blob);
      objectUrlRef.current = objUrl;
      setData(objUrl);

      return { data: objUrl, fileType };
    } catch (error) {
      catchError({
        error,
        default: {
          message: t('error.message.operationFailed'),
          description: t('error.message.downloadFile')
        },
        additionalErrorMessageMap: {
          [StatusCodes.FORBIDDEN]: {
            message: t(NO_USER_PERMISSION_MESSAGE_TEXT)
          }
        }
      });
      onError?.();
      return { data: null, fileType: undefined };
    } finally {
      setIsLoading(false);
    }
  }, [url, isLoading, catchError, t, onError, revokeCurrentObjectUrl]);

  return { data, fileType, isLoading, loadFile };
};

export default useFileLoader;
